import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { combineLatest, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';

import {
    ga4ContextProcessConfig,
} from '../helpers/ga4-context.helper';
import { ga4EcommerceProcessConfig, isBundle } from '../helpers/ga4-ecommerce.helper';
import { ga4EventDataProcessConfig } from '../helpers/ga4-event-data.helper';
import { ga4UserProcessConfig } from '../helpers/ga4-user.helper';
import {
    CustomContext,
    GA4EventConfiguration,
    Ga4EventData,
    GA4EventDataObservables,
    GA4EventDataParam,
    GA4Selectors,
} from '../models/configuration.model';
import { GA4EcommerceObservables, GA4EcommerceParam, GA4EcommerceValues } from '../models/ecommerce.model';
import { GA4EventDataValues } from '../models/event-data.model';
import { GA4Event, GA4EventType } from '../models/general.model';
import {
    getCustomContextSelector,
    getGridPageProductsFiltered,
    getLastBundleReference,
} from '../store/ga4-analytics.selectors';

import { GA4ProductOriginHelpers } from './ga4-product-origin.helpers';

import { Product } from '@api/catalog/products/products.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { CoreStorageService } from '@core/services/storage/storage.service';
import { CategoriesSelectors } from '@features/category';
import { MenuCategory } from '@features/category/domain/models/menu-category.model';
import { selectOrder } from '@features/order-confirmation/infrastructure/state/order-confirmation.selectors';
import { PublicUser } from '@features/user';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { getTotalQuantityProducts } from '@pages/category/modules/category-grid/store/category-grid.selectors';
import { getCustomCategoryTotalQuantityProducts } from '@pages/category/modules/grid-custom-category/store/custom-category-grid.selectors';
import { getSelectedDropPoint } from '@pages/checkout/shipping/store/pickup/pickup.selectors';
import { getCoupons } from '@pages/checkout/summary/store/summary.selectors';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { getIsFastSint } from '@shared/fast-sint/store/fast-sint.selectors';
import { getCheckoutAuth } from '@shared/navigation/store/navigation.selectors';
import { getPaymentDetails, getSelectedDiscount } from '@shared/payment/store';
import { getRelatedProductsPlaces } from '@shared/product/related-products/application/store/related-products.selectors';
import { getSelectedShippingMethod } from '@shared/shipping/store/shipping.selectors';
import {
    getAdjustments,
    getGiftTicket,
    getTotalOrder,
    getTotalProduct,
} from '@shared/shopcart/store/shopcart.selectors';
import { getShopCartCoreId, getShopCartCoreItems } from '@shared/shopcart-core/store/shopcart-core.selectors';
import { getUserSelector } from '@shared/user/store';

@Injectable()
export class GA4EventParserService {
    private readonly getUser$: Observable<PublicUser> = this.store.select(getUserSelector);

    private readonly getCustomContext$: Observable<CustomContext> = combineLatest([
        this.store.select(getCustomContextSelector),
        this.store.select(CategoriesSelectors.getCategoriesNavigation),
    ]).pipe(
        map(([context, categoriesNavigation]: [CustomContext, MenuCategory[]]) => {
            return {
                ...context,
                categoryInfo: {
                    ...context.categoryInfo,
                    categoriesNavigation,
                },
            };
        })
    );

    constructor(
        private readonly store: Store,
        private readonly coreStorageService: CoreStorageService,
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(WINDOW) private readonly window: CommonWindow,
    ) {
    }

    public generateSelectors(config: GA4EventConfiguration, eventFrom: EventFrom): GA4Selectors {
        return {
            ecommerce: this.parseEcomerceSelectors(config.ecommerce),
            user: of(null),
            customContext: this.getCustomContext$, // demomento no discutible
            eventData: this.parseEventDataSelectors(config.eventData, eventFrom),
        };
    }

    public prepareGroupParams(
        config: GA4EventConfiguration,
        eventFrom: EventFrom,
        data?: Ga4EventData,
    ): Observable<GA4Event> {
        const ga4Selectors: GA4Selectors = this.generateSelectors(config, eventFrom);

        if (config.user) {
            ga4Selectors.user = this.getUser$;
        }

        return forkJoin({
            ecommerce: Object.keys(ga4Selectors.ecommerce).length
                ? combineLatest(ga4Selectors.ecommerce as any).pipe(first())
                : of({}),
            eventData: Object.keys(ga4Selectors.eventData).length
                ? combineLatest(ga4Selectors.eventData as any).pipe(first())
                : of({}),
            user: ga4Selectors.user.pipe(first()),
            customContext: ga4Selectors.customContext.pipe(first()),
        })
            .pipe(
                map(({
                    ecommerce, user, customContext, eventData,
                }: {
                    ecommerce: GA4EcommerceValues;
                    user: PublicUser;
                    customContext: CustomContext;
                    eventData: GA4EventDataValues;
                }) => {
                    const eventDataInfo: GA4EventDataValues = this.getEventDataInfoByParam(
                        config.name,
                        config.eventData,
                        { ...eventData, eventFrom },
                        customContext,
                        data,
                    );

                    const ecommerceValues: GA4EcommerceValues = {
                        ...ecommerce,
                        ...data,
                        items: data?.items || ecommerce?.items,
                        category: data?.category
                            || customContext.categoryInfo.lookbookCategory
                            || ecommerce?.category,
                        customContext,
                        eventFrom,
                        eventType: config.name,
                    };

                    const event: GA4Event = {
                        event: config.name,
                        ecommerce: ga4EcommerceProcessConfig(config.ecommerce, ecommerceValues),
                        eventData: ga4EventDataProcessConfig(config.eventData, eventDataInfo),
                    };

                    if (config.user) {
                        const gaCookie: string = this.coreStorageService.getValue('_ga');
                        event.user = ga4UserProcessConfig(
                            config.user,
                            { user, store: customContext.context.store, gaCookie }
                        );
                    }
                    if (config.context) {
                        event.context = ga4ContextProcessConfig(
                            config.context,
                            {
                                user, customContext, document: this.document, window: this.window, eventFrom,
                            }
                        );
                    }
                    return event;
                }),
                catchError((error: unknown) => {
                    console.error(error);
                    return throwError(() => error);
                })
            );
    }

    private getEventDataInfoByParam(
        eventType: GA4EventType,
        config: GA4EventDataParam[],
        eventData: GA4EventDataValues,
        customContext: CustomContext,
        data?: Ga4EventData
    ): GA4EventDataValues {
        let eventDataResult: GA4EventDataValues = { ...eventData, items: data?.items, eventType };
        config?.forEach((param: GA4EventDataParam) => {
            switch (param) {
                case GA4EventDataParam.content_id:
                    eventDataResult.contentId = data.contentId;
                    break;
                case GA4EventDataParam.content_type:
                    eventDataResult.contentType = data.contentType;
                    break;
                case GA4EventDataParam.content_value:
                    eventDataResult.contentValue = data.contentValue;
                    break;
                case GA4EventDataParam.im_product_color:
                case GA4EventDataParam.im_product_size:
                    eventDataResult.items = data?.items;
                    break;
                case GA4EventDataParam.im_carousel_index:
                    eventDataResult.imCarouselIndex = data?.imCarouselIndex;
                    break;
                case GA4EventDataParam.method:
                    eventDataResult.method = data?.method;
                    break;
                case GA4EventDataParam.im_event_data_cf:
                    eventDataResult.imEventDataCf = data?.imEventDataCf;
                    eventDataResult.modal = customContext.modal;
                    break;
                case GA4EventDataParam.search_term:
                    eventDataResult.searchInfo = { ...customContext.searchInfo };
                    break;
                case GA4EventDataParam.im_items_number:
                    eventDataResult.searchInfo = { ...customContext.searchInfo };
                    break;
                case GA4EventDataParam.im_location:
                    eventDataResult.imLocation = data?.imLocation;
                    eventDataResult.modal = customContext.modal;
                    eventDataResult.page = customContext.page;
                    break;
                case GA4EventDataParam.im_type:
                    eventDataResult.imType = data?.imType;
                    eventDataResult.newsletterForm = data?.newsletterForm;
                    eventDataResult.modal = customContext.modal;
                    eventDataResult.searchInfo = { ...customContext.searchInfo };
                    eventDataResult.errorType = data?.errorType;
                    eventDataResult.filterEmitter = data?.filterEmitter;
                    break;
                case GA4EventDataParam.im_collection_reference:
                    eventDataResult.category = { ...customContext.categoryInfo.category };
                    break;
                case GA4EventDataParam.im_detail:
                    eventDataResult.errorData = data?.errorData;
                    eventDataResult.errorDescription = data?.errorDescription;
                    eventDataResult.errorType = data?.errorType;
                    eventDataResult.filterEmitter = data?.filterEmitter;
                    eventDataResult.formName = data?.formName;
                    eventDataResult.formValues = data?.formValues;
                    eventDataResult.imDetail = data?.imDetail;
                    eventDataResult.items = data?.items;
                    break;
                case GA4EventDataParam.im_link_page_title:
                    eventDataResult.modal = customContext.modal;
                    eventDataResult.page = customContext.page;
                    eventDataResult.lastSection = customContext.lastSection;
                    eventDataResult.product = customContext.product;
                    eventDataResult.router = customContext.router;
                    eventDataResult.shopCartData = customContext.shopCartData;
                    eventDataResult.category = customContext.categoryInfo.category;
                    eventDataResult.categoriesNavigation = customContext.categoryInfo.categoriesNavigation;
                    eventDataResult.lookbookCategory = customContext.categoryInfo.lookbookCategory;
                    break;
                case GA4EventDataParam.link_text:
                    eventDataResult.categoriesNavigation = customContext.categoryInfo.categoriesNavigation;
                    eventDataResult.category = customContext.categoryInfo.category;
                    eventDataResult.linkText = data?.imLinkText ?? eventDataResult.linkText;
                    break;
                case GA4EventDataParam.link_url:
                    eventDataResult.categoriesNavigation = customContext.categoryInfo.categoriesNavigation;
                    eventDataResult.category = customContext.categoryInfo.category;
                    eventDataResult.locationOrigin = this.window?.location?.origin;
                    break;
                case GA4EventDataParam.im_link_category:
                    eventDataResult.categoriesNavigation = customContext.categoryInfo.categoriesNavigation;
                    eventDataResult.category = customContext.categoryInfo.category;
                    eventDataResult.linkUrl = data?.linkUrl;
                    break;
                case GA4EventDataParam.im_link_type:
                    eventDataResult.imLinkType = data?.imLinkType;
                    break;
                case GA4EventDataParam.im_action_type:
                    eventDataResult.imActionType = data?.imActionType;
                    break;
                case GA4EventDataParam.im_category_view_type:
                    eventDataResult.category_view_type = data?.category_view_type;
                    break;
                case GA4EventDataParam.im_photo_type:
                    eventDataResult.imPhotoType = data?.imPhotoType;
                    break;
                case GA4EventDataParam.im_photo_reference:
                    eventDataResult.mediaPath = data?.mediaPath;
                    break;
                case GA4EventDataParam.im_category_cross_type:
                    eventDataResult.relatedProductType = data?.relatedProductType;
                    break;
                case GA4EventDataParam.im_bundle_reference:
                    if (eventType === GA4EventType.select_item) {
                        eventDataResult.imBundleReference = data?.imBundleReference;
                    } else if (eventType === GA4EventType.add_to_cart) {
                        const product = data?.items[0] as Product;
                        eventDataResult.imBundleReference = product.bundleReference || undefined;
                    } else {
                        eventDataResult.imBundleReference = isBundle(data?.items?.[0] as Product)
                            ? (data?.items[0] as Product).reference
                            : undefined;
                    }
                    break;
                default:
                    break;
            }
        });

        return eventDataResult;
    }

    private parseEcomerceSelectors(
        config: GA4EcommerceParam[],
    ): GA4EcommerceObservables {
        const obsObject$: GA4EcommerceObservables = {};
        config?.forEach((param) => {
            switch (param) {
                case GA4EcommerceParam.coupon:
                    obsObject$[GA4EcommerceParam.coupon] = this.store.select(getCoupons);
                    break;
                case GA4EcommerceParam.creative_slot:
                    break;
                case GA4EcommerceParam.im_cart_id:
                    obsObject$[GA4EcommerceParam.im_cart_id] = this.store.select(getShopCartCoreId);
                    break;
                case GA4EcommerceParam.im_cart_number_items:
                    obsObject$[GA4EcommerceParam.im_cart_number_items] = this.store.select(getShopCartCoreItems);
                    break;
                case GA4EcommerceParam.im_discount_type:
                    obsObject$[GA4EcommerceParam.im_discount_type] = this.store.select(getAdjustments);
                    obsObject$.orderConfirmationOrder = this.store.select(selectOrder);
                    break;
                case GA4EcommerceParam.im_drop_point:
                    obsObject$[GA4EcommerceParam.im_drop_point] = this.store.select(getSelectedDropPoint);
                    obsObject$[GA4EcommerceParam.im_shipping_tier] = this.store.select(getSelectedShippingMethod);
                    obsObject$.orderConfirmationOrder = this.store.select(selectOrder);
                    break;
                case GA4EcommerceParam.im_funnel_type:
                    obsObject$.isFastSint = this.store.select(getIsFastSint);
                    break;
                case GA4EcommerceParam.im_gift_card_type:
                case GA4EcommerceParam.im_gift_card_used:
                case GA4EcommerceParam.im_gift_card_value:
                    obsObject$.discount_data = this.store.select(getSelectedDiscount);
                    obsObject$.orderConfirmationOrder = this.store.select(selectOrder);
                    break;
                case GA4EcommerceParam.im_items_number:
                    obsObject$.categoryProductsIdsLength = this.store.select(getTotalQuantityProducts);
                    obsObject$.customCategoryProductsIdsLength = this.store.select(getCustomCategoryTotalQuantityProducts);
                    break;
                case GA4EcommerceParam.im_link_type:
                    break;
                case GA4EcommerceParam.im_payment_gift_ticket:
                    obsObject$[GA4EcommerceParam.im_payment_gift_ticket] = this.store.select(getGiftTicket);
                    obsObject$.orderConfirmationOrder = this.store.select(selectOrder);
                    break;
                case GA4EcommerceParam.item_list_id:
                case GA4EcommerceParam.item_list_name:
                case GA4EcommerceParam.items:
                    obsObject$.lastUrl = this.store.select(routerPublicQueries.getLastUrlFromHistory);
                    obsObject$.filteredProductsIds = this.store.select(getGridPageProductsFiltered);
                    obsObject$[GA4EcommerceParam.items] = this.store.select(getShopCartCoreItems).pipe(
                        map((coreItems) => {
                            if (coreItems) {
                                if (!Array.isArray(coreItems)) return [coreItems];
                                return coreItems;
                            }
                            return [];
                        }),
                    );
                    obsObject$.lastBundleReference = this.store.select(getLastBundleReference);
                    obsObject$.orderConfirmationOrder = this.store.select(selectOrder);
                    obsObject$.relatedProductsPlaces = this.store.select(getRelatedProductsPlaces);
                    obsObject$.relatedProductsPlaces = this.store.select(getRelatedProductsPlaces);
                    obsObject$.productOriginData = of(GA4ProductOriginHelpers.getProductOriginPersisting(
                        this.coreStorageService
                    ));
                    obsObject$.sourcePromotion = of(GA4ProductOriginHelpers.getSourcePromotion(this.coreStorageService));
                    break;
                case GA4EcommerceParam.location_id:
                    break;
                case GA4EcommerceParam.payment_type:
                case GA4EcommerceParam.im_payment_type:
                    obsObject$[GA4EcommerceParam.im_payment_type] = this.store.select(getPaymentDetails);
                    obsObject$.orderConfirmationOrder = this.store.select(selectOrder);
                    break;
                case GA4EcommerceParam.promotion_id:
                    break;
                case GA4EcommerceParam.promotion_name:
                    // se debería cargar el categoriesNavigation para buscar la categoria sin recursividad,
                    obsObject$.categories = this.store.select(CategoriesSelectors.getMenuCategories);
                    break;
                case GA4EcommerceParam.shipping:
                    obsObject$.orderConfirmationOrder = this.store.select(selectOrder);
                    break;
                case GA4EcommerceParam.shipping_tier:
                case GA4EcommerceParam.im_shipping_tier:
                    obsObject$[GA4EcommerceParam.im_shipping_tier] = this.store.select(getSelectedShippingMethod);
                    obsObject$.orderConfirmationOrder = this.store.select(selectOrder);
                    break;
                case GA4EcommerceParam.tax:
                    obsObject$.orderConfirmationOrder = this.store.select(selectOrder);
                    break;
                case GA4EcommerceParam.transaction_id:
                    obsObject$.orderConfirmationOrder = this.store.select(selectOrder);
                    break;
                case GA4EcommerceParam.value:
                    obsObject$.orderConfirmationOrder = this.store.select(selectOrder);
                    obsObject$.totalOrder = this.store.select(getTotalOrder);
                    obsObject$.totalProducts = this.store.select(getTotalProduct);
                    break;
                case GA4EcommerceParam.im_link_text:
                    break;
                default:
                    break;
            }
        });
        return obsObject$;
    }

    private parseEventDataSelectors(
        config: GA4EventDataParam[] | undefined,
        eventFrom: EventFrom | undefined
    ): GA4EventDataObservables {
        const obsObject$: GA4EventDataObservables = {};
        config?.forEach((param) => {
            switch (param) {
                case GA4EventDataParam.content_type:
                    break;
                case GA4EventDataParam.im_bundle_reference:
                    break;
                case GA4EventDataParam.im_collection_reference:
                    break;
                case GA4EventDataParam.im_event_data_cf:
                    if (eventFrom === EventFrom.login || eventFrom === EventFrom.signUp) {
                        obsObject$.checkoutAuth = this.store.select(getCheckoutAuth);
                    }
                    break;
                case GA4EventDataParam.im_items_number:
                    obsObject$.categoryProductsIdsLength = this.store.select(getTotalQuantityProducts);
                    obsObject$.customCategoryProductsIdsLength = this.store.select(getCustomCategoryTotalQuantityProducts);
                    break;
                case GA4EventDataParam.im_location:
                    break;
                case GA4EventDataParam.im_photo_reference:
                    break;
                case GA4EventDataParam.im_physical_store_id:
                case GA4EventDataParam.im_type:
                case GA4EventDataParam.item_id:
                    break;
                case GA4EventDataParam.method:
                    break;
                default:
                    break;
            }
        });
        return obsObject$;
    }
}
